import { useCallback, useEffect, useRef } from 'react';
import type { DrawStroke, DrawTool, FramePoint } from '@kinop/shared';
import { LIMITS } from '@kinop/shared';

export interface BrushSettings {
  tool: DrawTool;
  color: string;
  /** Толщина в долях ширины кадра. */
  size: number;
  eraser: boolean;
  neon: boolean;
  stamp: string;
}

interface DrawCanvasProps {
  strokes: DrawStroke[];
  /** Через сколько секунд линия растворяется. */
  trailSeconds: number;
  serverNow: () => number;
  active: boolean;
  brush: BrushSettings;
  onStroke: (stroke: Omit<DrawStroke, 'id' | 'authorClientId' | 'createdAt'>) => void;
}

/** Сколько последних секунд жизни штрих гаснет, а не исчезает разом. */
const FADE_MS = 1200;

/** Точки ближе этого друг к другу в штрих не пишем: их некуда девать. */
const MIN_STEP = 0.004;

/**
 * Холст поверх кадра.
 *
 * Рисует всё, что нарисовала комната, и то, что человек ведёт прямо сейчас.
 * Свой штрих показывается сразу и не ждёт ответа сервера: линия, отстающая
 * от пальца на круг по сети, ощущается сломанной. На сервер он уходит один
 * раз — когда штрих закончен.
 *
 * Координаты у штрихов в долях кадра, поэтому один и тот же рисунок ложится
 * и на полноэкранное видео, и на колонку в 300 пикселей.
 */
export function DrawCanvas({
  strokes,
  trailSeconds,
  serverNow,
  active,
  brush,
  onStroke,
}: DrawCanvasProps): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const localRef = useRef<FramePoint[] | null>(null);
  const brushRef = useRef(brush);
  brushRef.current = brush;

  // Данные для отрисовки держим в ref: такт рисования не должен зависеть
  // от перерисовок React, иначе линия рвётся на каждом новом штрихе комнаты.
  const strokesRef = useRef(strokes);
  strokesRef.current = strokes;
  const trailRef = useRef(trailSeconds);
  trailRef.current = trailSeconds;
  const serverNowRef = useRef(serverNow);
  serverNowRef.current = serverNow;

  const paint = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    const { width, height } = canvas;
    ctx.clearRect(0, 0, width, height);

    const now = serverNowRef.current();
    const lifetime = trailRef.current * 1000;

    const drawOne = (stroke: DrawStroke, alpha: number): void => {
      ctx.globalAlpha = alpha;
      // Ластик стирает по-настоящему: вырезает из уже нарисованного,
      // а не закрашивает его цветом фона поверх видео.
      ctx.globalCompositeOperation = stroke.eraser ? 'destination-out' : 'source-over';
      ctx.shadowBlur = stroke.neon && !stroke.eraser ? stroke.size * width * 1.4 : 0;
      ctx.shadowColor = stroke.color;
      ctx.strokeStyle = stroke.color;
      ctx.fillStyle = stroke.color;
      ctx.lineWidth = Math.max(1, stroke.size * width);
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';

      const points = stroke.points;
      if (points.length === 0) return;

      if (stroke.tool === 'stamp') {
        const size = Math.max(12, stroke.size * width * 6);
        ctx.font = `${size}px 'Apple Color Emoji','Segoe UI Emoji','Noto Color Emoji',serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(stroke.char ?? '❤️', points[0]!.x * width, points[0]!.y * height);
        return;
      }

      if (stroke.tool === 'spray') {
        // Баллончик — облако точек вокруг линии, а не сама линия.
        const radius = stroke.size * width * 2.2;
        for (const point of points) {
          for (let i = 0; i < 6; i++) {
            // Разброс детерминированный: у всех участников облако одинаковое.
            const seed = (point.x * 7919 + point.y * 104729 + i * 31) % 1;
            const angle = seed * Math.PI * 2;
            const distance = ((seed * 977) % 1) * radius;
            ctx.globalAlpha = alpha * (0.25 + ((seed * 31) % 1) * 0.5);
            ctx.beginPath();
            ctx.arc(
              point.x * width + Math.cos(angle) * distance,
              point.y * height + Math.sin(angle) * distance,
              Math.max(1, stroke.size * width * 0.3),
              0,
              Math.PI * 2,
            );
            ctx.fill();
          }
        }
        return;
      }

      if (points.length === 1) {
        ctx.beginPath();
        ctx.arc(points[0]!.x * width, points[0]!.y * height, ctx.lineWidth / 2, 0, Math.PI * 2);
        ctx.fill();
        return;
      }

      // Через квадратичные кривые, а не отрезками: ломаная выдаёт частоту
      // опроса указателя, а плавная линия выглядит как проведённая рукой.
      ctx.beginPath();
      ctx.moveTo(points[0]!.x * width, points[0]!.y * height);
      for (let i = 1; i < points.length; i++) {
        const prev = points[i - 1]!;
        const point = points[i]!;
        ctx.quadraticCurveTo(
          prev.x * width,
          prev.y * height,
          ((prev.x + point.x) / 2) * width,
          ((prev.y + point.y) / 2) * height,
        );
      }
      ctx.stroke();
    };

    for (const stroke of strokesRef.current) {
      const age = now - stroke.createdAt;
      if (age >= lifetime) continue;
      // Последнюю секунду жизни штрих тает, а не пропадает мгновенно.
      const remaining = lifetime - age;
      drawOne(stroke, remaining < FADE_MS ? Math.max(0, remaining / FADE_MS) : 1);
    }

    const local = localRef.current;
    if (local && local.length > 0) {
      drawOne(
        {
          id: 'local',
          authorClientId: 'local',
          tool: brushRef.current.tool,
          color: brushRef.current.color,
          size: brushRef.current.size,
          eraser: brushRef.current.eraser,
          neon: brushRef.current.neon,
          points: local,
          char: brushRef.current.stamp,
          createdAt: now,
        },
        1,
      );
    }

    ctx.globalAlpha = 1;
    ctx.shadowBlur = 0;
    ctx.globalCompositeOperation = 'source-over';
  }, []);

  // ─────────────────────────── размер холста ───────────────────────────

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const resize = (): void => {
      const rect = canvas.getBoundingClientRect();
      // Плотность экрана учитываем: на телефоне линия иначе выходит мыльной.
      const scale = Math.min(2, window.devicePixelRatio || 1);
      const width = Math.max(1, Math.round(rect.width * scale));
      const height = Math.max(1, Math.round(rect.height * scale));
      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
      }
      paint();
    };

    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(canvas);
    return () => observer.disconnect();
  }, [paint]);

  // ─────────────────────────── такт отрисовки ───────────────────────────

  useEffect(() => {
    let frame = 0;
    let lastPaint = 0;
    let dirty = true;
    const loop = (time: number): void => {
      const hasDrawing = strokesRef.current.length > 0 || !!localRef.current;
      if (!document.hidden && time - lastPaint >= 33 && (hasDrawing || dirty)) {
        paint(); lastPaint = time; dirty = hasDrawing;
      }
      frame = requestAnimationFrame(loop);
    };
    frame = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(frame);
  }, [paint]);

  // ─────────────────────────── ввод ───────────────────────────

  const pointAt = (event: React.PointerEvent<HTMLCanvasElement>): FramePoint => {
    const rect = event.currentTarget.getBoundingClientRect();
    return {
      x: Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width)),
      y: Math.min(1, Math.max(0, (event.clientY - rect.top) / rect.height)),
    };
  };

  const finish = useCallback(() => {
    const points = localRef.current;
    localRef.current = null;
    if (!points || points.length === 0) return;

    const { tool, color, size, eraser, neon, stamp } = brushRef.current;
    onStroke({
      tool,
      color,
      size,
      eraser,
      neon,
      points: points.slice(0, LIMITS.strokeMaxPoints),
      ...(tool === 'stamp' ? { char: stamp } : {}),
    });
  }, [onStroke]);

  return (
    <canvas
      ref={canvasRef}
      className="draw-canvas"
      data-active={active || undefined}
      onPointerDown={(event) => {
        if (!active) return;
        // Захват указателя: линия продолжается, даже если палец ушёл за край.
        event.currentTarget.setPointerCapture(event.pointerId);
        const point = pointAt(event);
        localRef.current = [point];
        // Штамп ставится одним нажатием — вести его некуда.
        if (brushRef.current.tool === 'stamp') finish();
      }}
      onPointerMove={(event) => {
        const points = localRef.current;
        if (!active || !points) return;
        const point = pointAt(event);
        const last = points[points.length - 1]!;
        if (Math.hypot(point.x - last.x, point.y - last.y) < MIN_STEP) return;
        if (points.length < LIMITS.strokeMaxPoints) points.push(point);
      }}
      onPointerUp={finish}
      onPointerCancel={finish}
    />
  );
}
